import { useCallback, useEffect, useState } from "react";

import { useApi, ApiError } from "../api/client";
import { aggregate } from "./aggregate";
import { mockReportProfiles } from "./data/mockProfiles";
import type { CohortFilters, ReportAggregates } from "./reportTypes";

/**
 * Loads de-identified report aggregates for the current cohort filter.
 *
 * Production path: GET /api/v1/reports/aggregates?<filters>. The server enforces
 * super-admin (returns 403 otherwise) and returns AGGREGATES ONLY — never raw
 * whānau rows. See docs note in REPORTING.md "Server contract".
 *
 * Dev path: until that endpoint exists, a 404/network failure falls back to
 * computing the same aggregates from the bundled mock profiles, so the dashboard
 * is fully demonstrable with `npm run dev`. A 403 is NOT faked away — it surfaces
 * as `forbidden` so the gate behaviour is real. Force mock with
 * VITE_REPORTS_USE_MOCK=true.
 */
export type ReportDataState =
  | { kind: "loading" }
  | { kind: "ok"; data: ReportAggregates; source: "api" | "mock" }
  | { kind: "forbidden" }
  | { kind: "error"; message: string; status?: number };

const USE_MOCK =
  (import.meta.env.VITE_REPORTS_USE_MOCK as string | undefined) === "true";

function toQuery(f: CohortFilters): string {
  const params = new URLSearchParams();
  for (const v of f.location) params.append("location", v);
  for (const v of f.iwi) params.append("iwi", v);
  for (const v of f.ethnicity) params.append("ethnicity", v);
  for (const v of f.service) params.append("service", v);
  for (const v of f.durationBand) params.append("duration_band", v);
  const q = params.toString();
  return q ? `?${q}` : "";
}

export function useReportData(filters: CohortFilters): ReportDataState {
  const callApi = useApi();
  const [state, setState] = useState<ReportDataState>({ kind: "loading" });

  const load = useCallback(async () => {
    setState({ kind: "loading" });

    if (USE_MOCK) {
      setState({
        kind: "ok",
        data: aggregate(mockReportProfiles, filters),
        source: "mock",
      });
      return;
    }

    try {
      const data = await callApi<ReportAggregates>(
        `/reports/aggregates${toQuery(filters)}`,
      );
      setState({ kind: "ok", data, source: "api" });
    } catch (err) {
      // A real authorisation failure must not be masked by the mock fallback.
      if (err instanceof ApiError && err.status === 403) {
        setState({ kind: "forbidden" });
        return;
      }
      // Endpoint not built yet / offline: fall back to local aggregation in dev
      // so the dashboard is still usable. In production, surface the error.
      if (import.meta.env.DEV) {
        setState({
          kind: "ok",
          data: aggregate(mockReportProfiles, filters),
          source: "mock",
        });
        return;
      }
      if (err instanceof ApiError) {
        setState({ kind: "error", status: err.status, message: err.message });
      } else {
        const message = err instanceof Error ? err.message : "Unknown error";
        setState({ kind: "error", message });
      }
    }
    // callApi is a fresh closure each render; deliberately excluded (matches
    // the pattern in useCurrentUser / AuditLog).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    void load();
  }, [load]);

  return state;
}
